import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useReducer, useState } from 'react'
import { format } from 'date-fns'
import styled from 'styled-components'
import { Container } from '../../components/Animations'
import { ButtonLeadingIcon, PrimaryButton } from '../../components/Button'
import ConfirmButtonModal from '../../components/ConfirmButtonModal'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import PageHeader from '../../components/PageHeader'
import { SelectedMethodIcon } from '../../components/PaymentMethodDisplay'
import Table from '../../components/Table'
import TimeText from '../../components/TimeText'
import Dots from '../../icons/Dots'
import Radom, { WithdrawalAccount } from '../../state/Radom'
import { generateRoundFlagIconFromCountryCode } from '../../util/Flags'
import { getMethod } from '../../util/Managed'
import { infoToast, successToast, useTestMode } from '../../util/Util'
import CreateWithdrawalAccountModal from './CreateWithdrawalAccountModal'
import { formatWithdrawalAccount } from '../../components/Shared'
import {
  DashboardPage,
  InlinePill,
  PageActionRow,
  PageHeaderRow,
  SummaryLabel,
  SummaryMeta,
  SummaryStrip,
  SummaryStripItem,
  SummaryValue,
  Surface,
  SurfaceHeader,
  SurfaceTitle
} from '../../components/DashboardPrimitives'
import LoadingBar from '../../components/LoadingBar'

type AccountCategory = 'all' | 'crypto' | 'fiat'

const AccountCategoryControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border: 1px solid #e3e7ef;
  border-radius: 11px;
  background: #f7f9fc;
`

const AccountCategoryButton = styled.button<{ $active?: boolean }>`
  align-items: center;
  border: 0;
  border-radius: 9px;
  background: ${({ $active }) => $active ? '#fff' : 'transparent'};
  color: ${({ $active }) => $active ? '#10141f' : '#6f7686'};
  cursor: pointer;
  display: inline-flex;
  font-size: 11.5px;
  font-weight: 500;
  gap: 6px;
  line-height: 1;
  min-width: 76px;
  padding: 7px 10px;
  transition: background-color 0.16s ease, color 0.16s ease;

  &:hover {
    background: ${({ $active }) => $active ? '#fff' : '#eef2f8'};
    color: #10141f;
  }
`

const DestinationIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`

const DestinationIdentityText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

const DestinationName = styled.span`
  color: #20242d;
  font-size: 13.5px;
  font-weight: 500;
`

const DestinationMeta = styled.span`
  color: #7d8492;
  font-size: 12px;
  line-height: 1.35;
`

const TinyFlagStack = styled.div`
  align-items: center;
  display: flex;

  > * + * {
    margin-left: -5px;
  }
`

const Accounts = observer((): ReactElement => {
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateWithdrawalModalOpen, setIsCreateWithdrawalModalOpen] = useState(false)
  const [withdrawalAccounts, setWithdrawalAccounts] = useState<WithdrawalAccount[]>([])
  const [accountCategory, setAccountCategory] = useState<AccountCategory>('all')
  const [testMode] = useTestMode()
  const [closeDropdownFns, updateCloseDropdownFns] = useReducer((p: any, n: any) => ({ ...p, ...n }), {})

  const fetchWithdrawalAccounts = async (): Promise<void> => {
    setIsLoading(true)
    setWithdrawalAccounts([])
    Radom.listWithdrawalAccounts()
      .then((accs) => setWithdrawalAccounts(accs))
      .catch(err => {
        console.error('Failed to load withdrawal accounts', err)
        setWithdrawalAccounts([])
      })
      .finally(() => setIsLoading(false)
      )
  }

  useEffect(() => {
    fetchWithdrawalAccounts()
  }, [testMode])

  const filteredWithdrawalAccounts = withdrawalAccounts
    .filter(t => {
      if (!t.accountDetails.Crypto) {
        return !testMode
      }

      const method = getMethod(t.accountDetails.Crypto.network, t.accountDetails.Crypto.token)
      return testMode ? !!method.isTestnet : !method.isTestnet
    })

  const cryptoAccountCount = filteredWithdrawalAccounts.filter(a => !!a.accountDetails.Crypto).length
  const fiatAccountCount = filteredWithdrawalAccounts.filter(a => !!a.accountDetails.Fiat).length
  const latestDestination = [...filteredWithdrawalAccounts]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
  const visibleWithdrawalAccounts = filteredWithdrawalAccounts.filter(account => {
    if (accountCategory === 'all') {
      return true
    }

    if (accountCategory === 'crypto') {
      return !!account.accountDetails.Crypto
    }

    return !!account.accountDetails.Fiat
  })

  const listTitle = accountCategory === 'crypto'
    ? 'Crypto addresses'
    : accountCategory === 'fiat'
      ? 'Bank accounts'
      : 'Saved accounts'

  const emptyPlaceholderText = accountCategory === 'crypto'
    ? 'No crypto accounts yet.'
    : accountCategory === 'fiat'
      ? 'No bank accounts yet.'
      : 'No withdrawal accounts yet.'

  return <Container>
    <DashboardPage>
      <PageHeaderRow>
        <PageHeader
          title='Withdrawal accounts' />
        <PageActionRow>
          <PrimaryButton onClick={() => {
            setIsCreateWithdrawalModalOpen(true)
          }}>
            <ButtonLeadingIcon>+</ButtonLeadingIcon>
            <span>Create withdrawal account</span>
          </PrimaryButton>
        </PageActionRow>
      </PageHeaderRow>

      <Surface>
        <SurfaceHeader>
          <div>
            <SurfaceTitle>Saved accounts</SurfaceTitle>
          </div>
          <InlinePill $tone={testMode ? 'amber' : 'green'}>
            {testMode ? 'Test mode' : 'Live mode'}
          </InlinePill>
        </SurfaceHeader>
        <SummaryStrip>
          <SummaryStripItem>
            <SummaryLabel>Total accounts</SummaryLabel>
            <SummaryValue>
              {isLoading ? <LoadingBar style={{ height: 28, width: 64 }} /> : filteredWithdrawalAccounts.length}
            </SummaryValue>
            <SummaryMeta>Saved</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Crypto</SummaryLabel>
            <SummaryValue>
              {isLoading ? <LoadingBar style={{ height: 28, width: 64 }} /> : cryptoAccountCount}
            </SummaryValue>
            <SummaryMeta>Wallets</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Fiat</SummaryLabel>
            <SummaryValue>
              {isLoading ? <LoadingBar style={{ height: 28, width: 64 }} /> : fiatAccountCount}
            </SummaryValue>
            <SummaryMeta>Banks</SummaryMeta>
          </SummaryStripItem>
          <SummaryStripItem>
            <SummaryLabel>Latest added</SummaryLabel>
            <SummaryValue>
              {isLoading
                ? <LoadingBar style={{ height: 28, width: 76 }} />
                : latestDestination
                  ? format(new Date(latestDestination.createdAt), 'MMM d')
                  : 'None'}
            </SummaryValue>
            <SummaryMeta>
              {latestDestination ? latestDestination.name : 'No destinations created yet'}
            </SummaryMeta>
          </SummaryStripItem>
        </SummaryStrip>
      </Surface>

      <Surface>
        <SurfaceHeader>
          <div>
            <SurfaceTitle>{listTitle}</SurfaceTitle>
          </div>
          <AccountCategoryControl aria-label='Saved account type'>
            {[
              ['all', 'All'],
              ['crypto', 'Crypto'],
              ['fiat', 'Fiat']
            ].map(([value, label]) =>
              <AccountCategoryButton
                key={value}
                type='button'
                $active={accountCategory === value}
                onClick={() => setAccountCategory(value as AccountCategory)}
              >
                {value === 'crypto' && <SelectedMethodIcon method={getMethod('Bitcoin')} size={13} disableToolTip />}
                {value === 'fiat' && <TinyFlagStack>
                  {generateRoundFlagIconFromCountryCode('US', 13)}
                  {generateRoundFlagIconFromCountryCode('EU', 13)}
                </TinyFlagStack>}
                {label}
              </AccountCategoryButton>
            )}
          </AccountCategoryControl>
        </SurfaceHeader>
        <Table
          headers={['Account', 'Details', 'Created', ''].map(t => <span key={t}>{t}</span>)}
          tableContainerStyle={{
            gridTemplateColumns: 'minmax(250px, 1.1fr) minmax(320px, 1.2fr) minmax(140px, 0.6fr) minmax(60px, auto)'
          }}
          items={
            visibleWithdrawalAccounts.map(a => {
              const method = a.accountDetails.Crypto ? getMethod(a.accountDetails.Crypto.network, a.accountDetails.Crypto.token) : undefined
              const isFiatAccount = !!a.accountDetails.Fiat
              const fiatCountryCode = a.accountDetails.Fiat?.accountType.us ? 'US' : 'EU'
              const typeLabel = a.accountDetails.Crypto
                ? `${method?.name ?? a.accountDetails.Crypto.network}`
                : `${fiatCountryCode} bank account`

              return [
              <DestinationIdentity key={0}>
                {a.accountDetails.Crypto && method
                  ? <SelectedMethodIcon method={method} size={26} disableToolTip />
                  : generateRoundFlagIconFromCountryCode(fiatCountryCode, 24)}
                <DestinationIdentityText>
                  <DestinationName>{a.name}</DestinationName>
                  <DestinationMeta>{typeLabel}</DestinationMeta>
                </DestinationIdentityText>
              </DestinationIdentity>,
              <div key={3} style={{ minWidth: 0 }}>
                {formatWithdrawalAccount(a.accountDetails)}
              </div>,
              <TimeText key="txTime" date={new Date(a.createdAt)} />,
              <div key={a.id} onClick={e => e.stopPropagation()} style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>

                <Dropdown
                  noChevron
                  onCloseFn={f => updateCloseDropdownFns({ [a.id]: f })}
                  selectedContent={<Dots width={15} />}
                  dropdownContent={
                    <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                      <DropdownItem onClick={() => {
                        navigator.clipboard.writeText(a.id)
                        infoToast('Account ID copied to clipboard')
                        closeDropdownFns[a.id]()
                      }}>Copy account ID</DropdownItem>
                      <ConfirmButtonModal
                        title='Delete account'
                        description='Are you sure you want to delete this account?'
                        action='Delete account'
                        onConfirm={async () => {
                          Radom.deleteWithdrawalAccount(a.id)
                            .then(async () => await fetchWithdrawalAccounts())
                            .then(() => successToast('Account deleted'))
                        }}
                        ActionComponent={(props: any) => <DropdownItem {...props}>Delete account</DropdownItem>}
                      />
                    </div>

                  }
                />
              </div>
            ]}
            )
          }
          emptyPlaceholderText={emptyPlaceholderText}
          isLoading={isLoading} />
      </Surface>

      {
        isCreateWithdrawalModalOpen &&
        <CreateWithdrawalAccountModal
          title='Create withdrawal account'
          visible={isCreateWithdrawalModalOpen}
          onClose={() => setIsCreateWithdrawalModalOpen(false)}
          onAccountCreated={() => {
            setIsCreateWithdrawalModalOpen(false)
            fetchWithdrawalAccounts()
          }} />
      }
    </DashboardPage>
  </Container>
})

export default Accounts
